import React, { useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import fr from 'facet-rest';
import { displayErrorMessage, displaySuccess } from 'Helpers/FacetNotificationManager';
import Button from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Button';
import LinearProgress from 'Components/UI/LinearProgress';
import Uploader from 'Components/Files/GenericUploader/Uploader';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import Dialog from 'Components/UI/ConfirmationDialog/Dialog';
import { noop } from 'Helpers/PropTypes';
import { DataValueMap } from 'Helpers/Testing';

const styles = theme => ({
  taskButton: {
    padding: theme.spacing(0, 1),
    marginRight: theme.spacing(1),
    borderRadius: '16px',
  },
  progressBar: {
    maxHeight: 21,
    margin: theme.spacing(0, 1),
    minWidth: 90,
    '& .MuiLinearProgress-root': {
      borderRadius: theme.radius.lg,
    },
  },
  modalButtons: {
    display: 'flex',
    justifyContent: 'center',
  },
});

const TaskUploader = ({
  className,
  classes,
  actionItemResource,
  fileCollection,
  setFileCollection,
  buttonOverride,
  onMarkComplete,
}) => {
  const [uploadProgress, setProgress] = useState(0);
  const [displayTaskCompleteModal, setDisplayTaskCompleteModal] = useState(false);
  if (!actionItemResource || !actionItemResource.relationship(fr.relationships.uploadSecureFiles.name)?.link('related').url()) return null;

  const button = buttonOverride || (
    <Button
      variant="contained"
      color="primary"
      size="xs"
      className={classNames(className, classes.taskButton)}
      {...DataValueMap.init('task-uploader-add-file-button').build()}
    >
      {'Add File'}
    </Button>
  );

  const markTaskComplete = async () => {
    const [actionItem] = await actionItemResource.link('self').get();
    actionItem.attr('status', 'completed');
    try {
      await actionItem.submit();
      onMarkComplete();
      setDisplayTaskCompleteModal(false);
      actionItemResource.attr('status', 'completed');
      displaySuccess('Marked complete');
    } catch (err) {
      displayErrorMessage(err, 'Failed to mark complete');
    }
  };

  return (
    <>
      <Uploader
        url={actionItemResource.relationship(fr.relationships.uploadSecureFiles.name).link('related').url()}
        setUploadProgress={(v) => {
          setProgress(v);
        }}
        onSuccess={() => {
          fileCollection.refresh().then((coll) => {
            setFileCollection(coll);
            setDisplayTaskCompleteModal(true);
          }).catch((err) => {
            displayErrorMessage(err,
              'Failed to refresh files, please try refreshing the page.');
          });
        }}
        successToastMessage="Successfully uploaded file(s)"
      >
        {uploadProgress ? (
          <LinearProgress
            color="accentGreen"
            className={classes.progressBar}
            visible
            text={uploadProgress === 100 ? 'Processing...' : ''}
            textVariant="body1"
            ProgressProps={{
              variant: 'determinate',
              value: uploadProgress,
            }}
          />
        ) : button}
      </Uploader>
      <Dialog
        open={displayTaskCompleteModal}
        title={'Mark this complete?'}
        confirmLabel={'Yes'}
        cancelLabel={'No'}
        onConfirm={markTaskComplete}
        onCancel={() => setDisplayTaskCompleteModal(false)}
        onClose={() => setDisplayTaskCompleteModal(false)}
        DialogActionsProps={{
          className: classes.modalButtons,
        }}
      />
    </>
  );
};

TaskUploader.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  actionItemResource: PropTypes.object.isRequired,
  fileCollection: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array,
  ]),
  setFileCollection: PropTypes.func.isRequired,
  buttonOverride: PropTypes.node,
  onMarkComplete: PropTypes.func,
};

TaskUploader.defaultProps = {
  className: '',
  buttonOverride: null,
  fileCollection: [],
  onMarkComplete: noop,
};

export default withStyles(styles)(TaskUploader);
